import { createSlice } from '@reduxjs/toolkit';

const initialState = {
    title: '',
    isEdit: false,
    customerId: '',
    productId: '',
    customerName: '',
    email: '',
    contactNumber: '',
    categoryObject: {},
    subCategoryObject: {},
    priorityObject: {},
    complaintTitle: '',
    complaintDescription: '',
    complaintReferenceNumber: '',
    id: '',
    complaintId: '',
    serialNo: '',
    rawStatus: '',
    noteLoading: false,
    noteSuccess: false,
    images: [],
    imagesLoading: false,
    imagesError: null,
    updateStatusLoading: false,
    updateStatusSuccess: false,
    setUpdateStatusFailure: null,
    setComplaintStatus: '',
    feedbackRemarks: '',
    feedbackRating: '',
    feedbackRequest: false,
    customerFeedback: '',
    complaintDataLoading: false,
    complaintDataError: null,
    complaintDataSuccess: false,
    isOnComplaintRoute: false,
};

const complaintSlice = createSlice({
    name: 'complaint',
    initialState,
    reducers: {
        setComplaintDetail: (state, action) => {
            const {
                title,
                isEdit,
                customerId,
                productId,
                productName,
                customerName,
                email,
                contactNumber,
                categoryObject,
                subCategoryObject,
                priorityObject,
                complaintTitle,
                complaintDescription,
                complaintReferenceNumber,
                complaintId,
                createdDate,
                warrantyStatus,
                description,
                serialNo,
                rawStatus,
                feedbackRating,
                feedbackRemarks,
                feedbackRequest,
                complaintSubCategory,
            } = action.payload;

            state.title = title;
            state.isEdit = isEdit || false;
            state.customerId = customerId;
            state.productId = productId;
            state.productName = productName;
            state.customerName = customerName;
            state.email = email;
            state.contactNumber = contactNumber;
            state.categoryObject = categoryObject;
            state.subCategoryObject = subCategoryObject;
            state.priorityObject = priorityObject;
            state.complaintTitle = complaintTitle;
            state.complaintDescription = complaintDescription;
            state.complaintReferenceNumber = complaintReferenceNumber;
            state.complaintId = complaintId;
            state.createdDate = createdDate;
            state.warrantyStatus = warrantyStatus;
            state.description = description;
            state.serialNo = serialNo;
            state.rawStatus = rawStatus;
            state.feedbackRating = feedbackRating;
            state.feedbackRemarks = feedbackRemarks;
            state.feedbackRequest = feedbackRequest;
            state.complaintSubCategory = complaintSubCategory;
        },

        // New action to set complete complaint data from API
        setCompleteComplaintData: (state, action) => {
            const data = action.payload;

            // Parse customMetadata to extract complaintRefNo
            let complaintRefNo = '';
            if (data.customMetadata) {
                try {
                    const metadata = typeof data.customMetadata === 'string' 
                        ? JSON.parse(data.customMetadata) 
                        : data.customMetadata;
                    complaintRefNo = metadata.complaintRefNo || '';
                } catch (e) {
                    console.error('Error parsing customMetadata:', e);
                }
            }

            // Set all complaint fields
            state.complaintId = data.id || '';
            state.id = data.id || '';
            state.title = data.title || '';
            state.description = data.description || '';
            state.serialNo = data.productSerial || '';
            state.complaintReferenceNumber = complaintRefNo || data.complaintReferenceNumber || '';
            state.categoryObject = data.categoryObject || {
                value: data.complaintCategory,
                label: data.complaintCategory
            };
            state.complaintSubCategoryObject = data.complaintSubCategoryObject || {
                value: data.complaintSubCategory,
                label: data.complaintSubCategory
            };
            state.complaintSubCategory = data.complaintSubCategory || '';
            state.rawStatus = data.status || '';
            state.customerName = data.customerName || '';
            state.email = data.email || '';
            state.contactNumber = data.contactNumber || '';
            state.productId = data.productId || '';
            state.productName = data.productName || '';
            state.feedbackRemarks = data.feedbackRemarks || '';
            state.feedbackRequest = data.feedbackRequest || false;
            state.feedbackRating = data.feedbackRating || '';
            state.customerFeedback = data.customerFeedback || '';
            state.images = data.images || [];
            state.complaintDataLoading = false;
            state.complaintDataError = null;
            state.complaintDataSuccess = true;
            state.imagesLoading = false;
            state.imagesError = null;
        },

        clearComplaintDetail: (state) => {
            state.title = '';
            state.isEdit = false;
            state.customerId = '';
            state.productId = '';
            state.productName = '';
            state.customerName = '';
            state.email = '';
            state.contactNumber = '';
            state.categoryObject = {};
            state.subCategoryObject = {};
            state.priorityObject = {};
            state.complaintTitle = '';
            state.complaintDescription = '';
            state.complaintReferenceNumber = '';
            state.complaintId = '';
            state.createdDate = '';
            state.warrantyStatus = '';
            state.description = '';
            state.serialNo = '';
            state.rawStatus = '';
            state.noteLoading = false;
            state.noteSuccess = false;
            state.feedbackRating = '';
            state.feedbackRemarks = '';
            state.feedbackRequest = false;
            state.complaintSubCategory = '';
            state.customerFeedback = '';
            state.complaintDataLoading = false;
            state.complaintDataError = null;
            state.complaintDataSuccess = false;
        },

        setComplaintId: (state, action) => {
            state.id = action.payload;
        },

        resetComplaintId: (state) => {
            state.id = '';
        },

        setEditComplaintId: (state, action) => {
            state.complaintId = action.payload;
        },

        resetEditComplaintId: (state) => {
            state.complaintId = '';
        },

        setNoteLoading: (state, action) => {
            state.noteLoading = action.payload;
        },

        setNoteSuccess: (state, action) => {
            state.noteSuccess = action.payload;
        },

        resetNoteState: (state) => {
            state.noteLoading = false;
            state.noteSuccess = false;
        },

        // New actions for complete complaint data loading states
        setComplaintDataLoading: (state, action) => {
            state.complaintDataLoading = action.payload;
            if (action.payload) {
                state.complaintDataError = null;
                state.complaintDataSuccess = false;
            }
        },

        setComplaintDataError: (state, action) => {
            state.complaintDataError = action.payload;
            state.complaintDataLoading = false;
            state.complaintDataSuccess = false;
        },

        setComplaintDataSuccess: (state, action) => {
            state.complaintDataSuccess = action.payload;
            if (action.payload) {
                state.complaintDataError = null;
            }
        },

        resetComplaintDataState: (state) => {
            state.complaintDataLoading = false;
            state.complaintDataError = null;
            state.complaintDataSuccess = false;
        },

        setIsOnComplaintRoute: (state, action) => {
            state.isOnComplaintRoute = action.payload;
        },

        setImagesLoading: (state, action) => {
            state.imagesLoading = action.payload;
            if (action.payload) {
                state.imagesError = null;
            }
        },

        setComplaintImages: (state, action) => {
            state.images = action.payload;
            state.imagesLoading = false;
            state.imagesError = null;
        },

        setImagesError: (state, action) => {
            state.imagesError = action.payload;
            state.imagesLoading = false;
        },

        clearComplaintImages: (state) => {
            state.images = [];
            state.imagesLoading = false;
            state.imagesError = null;
        },
        setUpdateStatusLoading: (state, action) => {
            state.updateStatusLoading = action.payload;
        },

        setUpdateStatusSuccess: (state, action) => {
            state.updateStatusSuccess = action.payload;
        },
        setUpdateStatusFailure: (state, action) => {
            state.setUpdateStatusFailure = action.payload;
        },
        resetUpdateStatusState: (state) => {
            state.updateStatusLoading = false;
            state.updateStatusSuccess = false;
        },
        setComplaintStatus: (state, action) => {
            state.rawStatus = action.payload;
        }
    },
});

export const {
    setComplaintDetail,
    setCompleteComplaintData,
    clearComplaintDetail,
    setComplaintId,
    resetComplaintId,
    setEditComplaintId,
    resetEditComplaintId,
    setNoteLoading,
    setNoteSuccess,
    resetNoteState,
    setComplaintDataLoading,
    setComplaintDataError,
    setComplaintDataSuccess,
    resetComplaintDataState,
    setIsOnComplaintRoute,
    setImagesLoading,
    setComplaintImages,
    setImagesError,
    clearComplaintImages,
    setUpdateStatusLoading,
    setUpdateStatusSuccess,
    resetUpdateStatusState,
    setUpdateStatusFailure,
    setComplaintStatus,
} = complaintSlice.actions;

export default complaintSlice.reducer;